// Menedżer artykułów bloga CMS

import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { PencilFill, Trash, PlusLg, Image } from 'react-bootstrap-icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { reduxActions, useDispatch } from 'store';
import { useGetPanelKnowledgeBase, useDeleteCmsArticle } from 'api';
import { Button, SearchInput, Pagination } from 'components/controls';
import { IPanelKnowledgeBaseItem } from 'api/endpoints/useGetPanelKnowledgeBase';

import BlogEditor from './components/BlogEditor/BlogEditor';

import styles from 'theme/pages/PanelBlog/PanelBlog.module.scss';

const PAGE_LIMIT = 20;

// ── ArticleRow ────────────────────────────────────────────────────────────────

interface IArticleRowProps {
  item: IPanelKnowledgeBaseItem;
  onEdit: (id: number) => void;
  onDeleted: () => void;
}

const ArticleRow: React.FC<IArticleRowProps> = ({ item, onEdit, onDeleted }) => {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { mutate: deleteArticle, isLoading: isDeleting } = useDeleteCmsArticle(item.id, {
    onSuccess: () => onDeleted()
  });

  const isImage = item.image && /\.(jpe?g|png|gif|webp|svg)$/i.test(item.image);

  return (
    <tr className={styles.tableRow}>
      {/* Thumbnail */}
      <td className={styles.td}>
        {isImage ? (
          <img src={item.image!} alt={t('Miniaturka')} className={styles.thumb} />
        ) : (
          <div className={styles.thumbPlaceholder}>
            <Image size={16} />
          </div>
        )}
      </td>

      {/* Title + symbol */}
      <td className={styles.td}>
        <p className={styles.articleTitle}>{item.title || item.symbol}</p>
        <span className={styles.articleSymbol}>{item.symbol}</span>
      </td>

      {/* Actions */}
      <td className={`${styles.td} ${styles.tdActions}`}>
        {confirmDelete ? (
          <div className={styles.deleteConfirm}>
            <span><Trans>Usunąć?</Trans></span>
            <Button color="danger" loading={isDeleting} onClick={() => deleteArticle()}>
              <Trans>Tak</Trans>
            </Button>
            <Button color="gray" onClick={() => setConfirmDelete(false)}>
              <Trans>Nie</Trans>
            </Button>
          </div>
        ) : (
          <div className={styles.actionsCell}>
            <button className={styles.btnEdit} onClick={() => onEdit(item.id)} title={t('Edytuj')}>
              <PencilFill size={12} />
              <Trans>Edytuj</Trans>
            </button>
            <button className={styles.btnDelete} onClick={() => setConfirmDelete(true)} title={t('Usuń')}>
              <Trash size={13} />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};

// ── Skeleton rows ─────────────────────────────────────────────────────────────

const SkeletonRows: React.FC = () => (
  <>
    {Array.from({ length: 6 }).map((_, i) => (
      <tr key={i} className={styles.tableRow}>
        <td className={styles.td}><div className={styles.skeletonThumb} /></td>
        <td className={styles.td}>
          <div className={styles.skeletonCell}>
            <div className={styles.skeletonLine} style={{ width: '60%' }} />
            <div className={styles.skeletonLine} style={{ width: '35%' }} />
          </div>
        </td>
        <td className={`${styles.td} ${styles.tdActions}`}>
          <div className={styles.actionsCell}>
            <div className={styles.skeletonBadge} style={{ width: 64 }} />
            <div className={styles.skeletonThumb} style={{ width: 32, height: 32, borderRadius: 6 }} />
          </div>
        </td>
      </tr>
    ))}
  </>
);

// ── PanelBlog ─────────────────────────────────────────────────────────────────

const PanelBlog = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { articleId: articleIdParam } = useParams<{ articleId: string }>();

  // Wyciągnij prefix języka z URL (np. "/pl")
  const langPrefix = pathname.split('/').slice(0, 2).join('/');
  const blogBase = `${langPrefix}/managment_panel/blog`;

  const isEditing = articleIdParam !== undefined;
  const editingId = isEditing
    ? articleIdParam === 'new' ? 'new' : parseInt(articleIdParam, 10)
    : null;

  const [searchPhrase, setSearchPhrase] = useState('');
  const [page, setPage] = useState(1);

  const { data, refetch, isLoading } = useGetPanelKnowledgeBase({
    page,
    limit: PAGE_LIMIT,
    ...(searchPhrase ? { search_keyword: searchPhrase } : {})
  });

  useEffect(() => {
    dispatch(
      reduxActions.setBreadcrumbs([
        { name: t('Panel'), path: 'managment_panel' },
        { name: t('Blog'), path: undefined }
      ])
    );
  }, []);

  const handleSearch = (phrase: string) => {
    setSearchPhrase(phrase);
    setPage(1);
  };

  if (editingId !== null) {
    return (
      <BlogEditor
        articleId={editingId === 'new' ? undefined : editingId}
        onBack={() => navigate(blogBase)}
        onSaved={(newId) => {
          refetch();
          if (newId) navigate(`${blogBase}/${newId}`);
        }}
      />
    );
  }

  const totalCount = data?.total_count ?? 0;
  const pagesCount = Math.ceil(totalCount / PAGE_LIMIT);

  return (
    <div className={styles.wrapper}>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}><Trans>Blog</Trans></h1>
          <p className={styles.subtitle}>
            {isLoading ? '…' : totalCount > 0 ? `${totalCount} ${t('artykułów')}` : t('Brak artykułów')}
          </p>
        </div>
        <div className={styles.headerRight}>
          <Button onClick={() => navigate(`${blogBase}/new`)}>
            <PlusLg size={14} style={{ marginRight: 4 }} />
            <Trans>Nowy artykuł</Trans>
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className={styles.searchRow}>
        <SearchInput
          value={searchPhrase}
          placeholder={t('Szukaj artykułu...')}
          onChange={handleSearch}
        />
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead className={styles.tableHead}>
            <tr>
              <th className={styles.th} style={{ width: 68 }}><Trans>Grafika</Trans></th>
              <th className={styles.th}><Trans>Tytuł / Symbol</Trans></th>
              <th className={`${styles.th} ${styles.thActions}`} style={{ width: 160 }}><Trans>Akcje</Trans></th>
            </tr>
          </thead>
          <tbody className={styles.tableBody}>
            {isLoading ? (
              <SkeletonRows />
            ) : !data?.items?.length ? (
              <tr>
                <td colSpan={3}>
                  <div className={styles.emptyState}>
                    <span className={styles.emptyIcon}>📝</span>
                    <span className={styles.emptyText}>
                      {searchPhrase
                        ? <Trans>Brak artykułów pasujących do wyszukiwania.</Trans>
                        : <Trans>Brak artykułów. Utwórz pierwszy artykuł bloga.</Trans>}
                    </span>
                    {!searchPhrase && (
                      <Button onClick={() => navigate(`${blogBase}/new`)}>
                        <Trans>Utwórz artykuł</Trans>
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              data.items.map((item) => (
                <ArticleRow
                  key={item.id}
                  item={item}
                  onEdit={(id) => navigate(`${blogBase}/${id}`)}
                  onDeleted={() => refetch()}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagesCount > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination page={page} pagesCount={pagesCount} onChange={(p) => setPage(p)} />
        </div>
      )}

    </div>
  );
};

export default PanelBlog;
